/**
 * Family Service
 *
 * Data access layer for family member operations.
 * Refactored to use generic API wrapper for DRY compliance.
 */

import { apiGet, apiGetOrNull, apiPost, apiPut, apiDelete, apiPostFormData } from '@/lib/api';
import type {
  FamilyMemberRow,
  FamilyMemberInsert,
  FamilyMemberUpdate,
  FamilyRelationshipRow,
  FamilyRelationshipInsert,
} from '@/types/database';

export interface FamilyMemberWithRelations extends FamilyMemberRow {
  relationships?: FamilyRelationshipRow[];
}

const CONTEXT = 'family';

export async function getFamilyMembers(userId: string): Promise<FamilyMemberWithRelations[]> {
  return apiGet<FamilyMemberWithRelations[]>(
    '/family/members',
    `${CONTEXT}.getFamilyMembers`,
    { defaultValue: [] }
  );
}

export async function getFamilyMemberById(id: string): Promise<FamilyMemberWithRelations | null> {
  return apiGetOrNull<FamilyMemberWithRelations>(
    `/family/members/${id}`,
    `${CONTEXT}.getFamilyMemberById`
  );
}

export async function createFamilyMember(member: FamilyMemberInsert): Promise<FamilyMemberRow> {
  return apiPost<FamilyMemberRow>('/family/members', `${CONTEXT}.createFamilyMember`, member);
}

export async function updateFamilyMember(
  id: string,
  updates: FamilyMemberUpdate
): Promise<FamilyMemberRow> {
  return apiPut<FamilyMemberRow>(
    `/family/members/${id}`,
    `${CONTEXT}.updateFamilyMember`,
    updates
  );
}

export async function deleteFamilyMember(id: string): Promise<void> {
  return apiDelete(`/family/members/${id}`, `${CONTEXT}.deleteFamilyMember`);
}

export async function getRelationships(userId: string): Promise<FamilyRelationshipRow[]> {
  return apiGet<FamilyRelationshipRow[]>(
    '/family/relationships',
    `${CONTEXT}.getRelationships`,
    { defaultValue: [] }
  );
}

export async function createRelationship(
  relationship: FamilyRelationshipInsert
): Promise<FamilyRelationshipRow> {
  return apiPost<FamilyRelationshipRow>(
    '/family/relationships',
    `${CONTEXT}.createRelationship`,
    relationship
  );
}

export async function deleteRelationship(id: string): Promise<void> {
  return apiDelete(`/family/relationships/${id}`, `${CONTEXT}.deleteRelationship`);
}

export async function getFamilyTree(userId: string): Promise<{
  members: FamilyMemberWithRelations[];
  relationships: FamilyRelationshipRow[];
}> {
  const [members, relationships] = await Promise.all([
    getFamilyMembers(userId),
    getRelationships(userId),
  ]);

  return { members, relationships };
}

export async function uploadFamilyPhoto(
  userId: string,
  memberId: string,
  file: File
): Promise<string> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiPostFormData<{ key: string; url: string }>(
    '/upload/family-photos',
    `${CONTEXT}.uploadFamilyPhoto`,
    formData
  );

  return response.key || response.url;
}

export async function updateTreePositions(
  userId: string,
  positions: Array<{ memberId: string; positionX: number; positionY: number }>
): Promise<void> {
  await Promise.all(
    positions.map((pos) =>
      apiPut(
        `/family/members/${pos.memberId}`,
        `${CONTEXT}.updateTreePositions`,
        { position_x: pos.positionX, position_y: pos.positionY }
      )
    )
  );
}

export async function deleteFamilyPhoto(photoUrl: string): Promise<void> {
  const urlParts = photoUrl.split('/family-photos/');
  if (urlParts.length < 2) return;

  const parts = urlParts[1].split('/');
  const filename = parts[parts.length - 1];

  return apiDelete(`/upload/family-photos/${filename}`, `${CONTEXT}.deleteFamilyPhoto`);
}

// ─── Per-member media: photos, videos, stories ─────────────────────────────
// Backend endpoints already exist at /family/members/:id/{photos|videos|stories}.

export interface FamilyMemberPhoto {
  id: string;
  family_member_id: string;
  photo_url: string;
  caption: string | null;
  created_at: string;
}

export interface FamilyMemberVideo {
  id: string;
  family_member_id: string;
  video_url: string;
  title: string | null;
  created_at: string;
}

export interface FamilyMemberStory {
  id: string;
  family_member_id: string;
  content: string;
  created_at: string;
}

export async function listMemberPhotos(memberId: string): Promise<FamilyMemberPhoto[]> {
  return apiGet<FamilyMemberPhoto[]>(
    `/family/members/${memberId}/photos`,
    `${CONTEXT}.listMemberPhotos`,
    { defaultValue: [] }
  );
}
export async function addMemberPhoto(
  memberId: string,
  photo_url: string,
  caption?: string | null
): Promise<FamilyMemberPhoto> {
  return apiPost<FamilyMemberPhoto>(
    `/family/members/${memberId}/photos`,
    `${CONTEXT}.addMemberPhoto`,
    { photo_url, caption: caption ?? null }
  );
}
export async function deleteMemberPhoto(memberId: string, photoId: string): Promise<void> {
  return apiDelete(
    `/family/members/${memberId}/photos/${photoId}`,
    `${CONTEXT}.deleteMemberPhoto`
  );
}

export async function listMemberVideos(memberId: string): Promise<FamilyMemberVideo[]> {
  return apiGet<FamilyMemberVideo[]>(
    `/family/members/${memberId}/videos`,
    `${CONTEXT}.listMemberVideos`,
    { defaultValue: [] }
  );
}
export async function addMemberVideo(
  memberId: string,
  video_url: string,
  title?: string | null
): Promise<FamilyMemberVideo> {
  return apiPost<FamilyMemberVideo>(
    `/family/members/${memberId}/videos`,
    `${CONTEXT}.addMemberVideo`,
    { video_url, title: title ?? null }
  );
}
export async function deleteMemberVideo(memberId: string, videoId: string): Promise<void> {
  return apiDelete(
    `/family/members/${memberId}/videos/${videoId}`,
    `${CONTEXT}.deleteMemberVideo`
  );
}

export async function listMemberStories(memberId: string): Promise<FamilyMemberStory[]> {
  return apiGet<FamilyMemberStory[]>(
    `/family/members/${memberId}/stories`,
    `${CONTEXT}.listMemberStories`,
    { defaultValue: [] }
  );
}
export async function addMemberStory(
  memberId: string,
  content: string
): Promise<FamilyMemberStory> {
  return apiPost<FamilyMemberStory>(
    `/family/members/${memberId}/stories`,
    `${CONTEXT}.addMemberStory`,
    { content }
  );
}
export async function deleteMemberStory(memberId: string, storyId: string): Promise<void> {
  return apiDelete(
    `/family/members/${memberId}/stories/${storyId}`,
    `${CONTEXT}.deleteMemberStory`
  );
}

export interface ExtractedFamilyData {
  name: string;
  relationship: string;
  occupation: string | null;
  hobbies: string[];
  funFacts: string | null;
  connectionDescription: string | null;
  isLiving: boolean;
}

export async function extractFromDescription(transcription: string): Promise<ExtractedFamilyData> {
  return apiPost<ExtractedFamilyData>(
    '/family/extract-from-description',
    `${CONTEXT}.extractFromDescription`,
    { transcription }
  );
}

export const familyService = {
  getMembers: getFamilyMembers,
  getMemberById: getFamilyMemberById,
  createMember: createFamilyMember,
  updateMember: updateFamilyMember,
  deleteMember: deleteFamilyMember,
  getRelationships,
  createRelationship,
  deleteRelationship,
  getTree: getFamilyTree,
  uploadPhoto: uploadFamilyPhoto,
  deletePhoto: deleteFamilyPhoto,
  updateTreePositions,
  extractFromDescription,
  // per-member media
  listMemberPhotos,
  addMemberPhoto,
  deleteMemberPhoto,
  listMemberVideos,
  addMemberVideo,
  deleteMemberVideo,
  listMemberStories,
  addMemberStory,
  deleteMemberStory,
};
